import { IconButton, Popover } from "@controls";
import { useLocalstorage } from "@hooks";
import { ArrowLeft, ArrowRight, Columns3, Minus, Plus } from "@icons";
import { useAnnounce } from "@src/components/live-announcer";
import { useReorderList } from "@src/components/reorder";
import { NO_INTERPOLATION_ESCAPE } from "@src/raya-lucaria/i18n/interpolation";
import { useEffect, useId } from "react";

import {
  COLUMN_RESIZE_STEP,
  DEFAULT_COLUMN_WIDTH,
  MAX_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
  clampColumnWidth,
} from "../column-width";
import { TableContextType, useTableContext } from "../context";
import Select from "../select";
import { getColumnLabel } from "../utils";

export default function TableColumnVisibilityToggle() {
  const { t, table, tableId, columnWidths, onColumnResize } =
    useTableContext() as TableContextType;
  const reorder = useReorderList();
  const idPrefix = useId();
  const { setState: setTableState } = table;
  const columns = table.getAllLeafColumns();
  const announce = useAnnounce();

  const [preservedColumnVisibility, setPreservedColumnVisibility] =
    useLocalstorage("TABLE-COLUMN-VISIBILITY-" + tableId?.toUpperCase(), "{}");

  useEffect(() => {
    if (!tableId) return;
    let preservedColumnVisibilityJson = {};
    try {
      preservedColumnVisibilityJson = JSON.parse(
        preservedColumnVisibility || "{}",
      );
    } catch (error) {
      console.error("Failed to parse preserved column visibility:", error);
    }

    setTableState((prev: any) => ({
      ...prev,
      columnVisibility: {
        ...prev.columnVisibility,
        ...preservedColumnVisibilityJson,
      },
    }));
  }, [preservedColumnVisibility, tableId, setTableState]);

  return (
    <Popover
      ariaLabel={t("toggleColumnVisibility")}
      trigger={
        <IconButton
          size="sm"
          icon={<Columns3 />}
          aria-label={t("toggleColumnVisibility")}
        />
      }
      onClose={() => {
        if (!tableId) return;
        const currentColumnVisibility = table.getState().columnVisibility;
        setPreservedColumnVisibility(
          JSON.stringify(currentColumnVisibility, null, 2),
        );
      }}
    >
      <div className="flex flex-col gap-2 m-1 max-h-[calc(var(--radix-popover-content-available-height)-3rem)]">
        <p className="font-semibold">{t("columns")}</p>
        <form className="min-h-0 overflow-y-auto px-3 -mx-3">
          {columns.map((column) => {
            const columnTitle = getColumnLabel(column);
            const toggleVisibility = column.getToggleVisibilityHandler();

            const isVisible = column.getIsVisible();
            const position = reorder.positionOf(column.id);
            const total = reorder.ids.length;
            const width = columnWidths[column.id] ?? DEFAULT_COLUMN_WIDTH;
            const nameOptions = {
              name: columnTitle,
              interpolation: NO_INTERPOLATION_ESCAPE,
            };
            const atStart = position <= 1;
            const atEnd = position >= total;
            const stepWidth = (direction: -1 | 1) => {
              const next = clampColumnWidth(
                width + direction * COLUMN_RESIZE_STEP,
              );
              if (next === width) return;
              onColumnResize(column.id, next - width);
              announce(
                t("columnWidthChanged", { ...nameOptions, width: next }),
              );
            };

            return (
              <div key={column.id} className="flex flex-col">
                <div className="flex min-h-target items-center gap-target-pitch-gap">
                  <Select
                    row
                    id={`${idPrefix}-${column.id}`}
                    checked={isVisible}
                    onChange={(event) => {
                      toggleVisibility(event);
                      announce(
                        event.target.checked
                          ? t("columnShown", {
                              column: columnTitle,
                              interpolation: NO_INTERPOLATION_ESCAPE,
                            })
                          : t("columnHidden", {
                              column: columnTitle,
                              interpolation: NO_INTERPOLATION_ESCAPE,
                            }),
                      );
                    }}
                  />
                  <label
                    htmlFor={`${idPrefix}-${column.id}`}
                    className="flex min-h-target flex-1 cursor-pointer items-center text-sm"
                  >
                    {columnTitle}
                  </label>
                </div>
                {isVisible && (
                  <div
                    role="group"
                    aria-label={columnTitle}
                    className="flex flex-wrap gap-target-pitch-gap"
                  >
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<ArrowLeft />}
                      tipLabel={t("reorderMoveLeftTip")}
                      label={t("reorderMoveColumnLeft", {
                        ...nameOptions,
                        position,
                        total,
                      })}
                      aria-disabled={atStart || reorder.saving}
                      className="aria-disabled:opacity-45"
                      onClick={() => reorder.moveBy(column.id, -1)}
                    />
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<ArrowRight />}
                      tipLabel={t("reorderMoveRightTip")}
                      label={t("reorderMoveColumnRight", {
                        ...nameOptions,
                        position,
                        total,
                      })}
                      aria-disabled={atEnd || reorder.saving}
                      className="aria-disabled:opacity-45"
                      onClick={() => reorder.moveBy(column.id, 1)}
                    />
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<Minus />}
                      tipLabel={t("columnNarrowerTip")}
                      label={t("columnNarrower", { ...nameOptions, width })}
                      aria-disabled={width <= MIN_COLUMN_WIDTH}
                      className="aria-disabled:opacity-45"
                      onClick={() => stepWidth(-1)}
                    />
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<Plus />}
                      tipLabel={t("columnWiderTip")}
                      label={t("columnWider", { ...nameOptions, width })}
                      aria-disabled={width >= MAX_COLUMN_WIDTH}
                      className="aria-disabled:opacity-45"
                      onClick={() => stepWidth(1)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </form>
      </div>
    </Popover>
  );
}
